const TZ_FALLBACK = 'America/Sao_Paulo';

/** 390 -> "6h30". Saldo negativo mantém o sinal. */
export function hm(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m}min`;
  return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, '0')}`;
}

/** 390 -> "06:30", para colunas de tabela alinhadas. */
export function hhmm(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minutes));
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

export function clockTime(iso: string, tz = TZ_FALLBACK): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

export function stamp(iso: string, tz = TZ_FALLBACK): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** "2026-09-18" -> "qui, 18 de set" */
export function prettyDate(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 12));
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  })
    .format(date)
    .replace('.,', ',');
}

export function longDate(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1, 12));
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export function todayKey(tz = TZ_FALLBACK): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
}

export function monthRange(ref = new Date(), tz = TZ_FALLBACK): { from: string; to: string } {
  const key = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(ref);
  const [y, m] = key.split('-').map(Number);
  const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

export function shiftMonth(from: string, delta: number): { from: string; to: string } {
  const [y, m] = from.split('-').map(Number);
  return monthRange(new Date(Date.UTC(y!, (m ?? 1) - 1 + delta, 15, 12)));
}

export function daysAgoRange(days: number, tz = TZ_FALLBACK): { from: string; to: string } {
  const to = todayKey(tz);
  const d = new Date();
  d.setDate(d.getDate() - days + 1);
  return { from: new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d), to };
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();
}

const ACTION_LABELS: Record<string, string> = {
  LOGIN: 'Entrou no sistema',
  LOGIN_DENIED: 'Login recusado',
  LOGOUT: 'Saiu do sistema',
  SEGMENT_START: 'Iniciou registro',
  SEGMENT_STOP: 'Encerrou registro',
  SEGMENT_CREATE: 'Lançou registro manual',
  SEGMENT_UPDATE: 'Editou registro',
  SEGMENT_DELETE: 'Excluiu registro',
  NOTE_UPSERT: 'Salvou atividade do dia',
  USER_CREATE: 'Criou usuário',
  USER_UPDATE: 'Alterou usuário',
  REPORT_EXPORT: 'Exportou relatório',
  CREDIT_CREATE: 'Abono de horas',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function actionTone(action: string): 'ok' | 'warn' | 'danger' | 'info' {
  if (action === 'SEGMENT_DELETE' || action === 'LOGIN_DENIED') return 'danger';
  if (action === 'SEGMENT_UPDATE' || action === 'USER_UPDATE') return 'warn';
  if (action === 'SEGMENT_START' || action === 'SEGMENT_STOP') return 'ok';
  return 'info';
}
